import type { JobAttachment, JobTask } from '../api/jobWorkflow'

const SUMMARY_PREFIX = 'rbcfs.jobSummary.v1.'
const TASKS_PREFIX = 'rbcfs.jobTasks.v1.'
const ATTACHMENTS_PREFIX = 'rbcfs.jobAttachments.v1.'

export type CachedJobSummary = {
  id: string
  job_number: string
  title: string
  description?: string | null
  service_type?: string | null
  contract_id?: string | null
  visit_group_id?: string | null
  is_recurring?: boolean | null
}

function readJson<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

function writeJson(key: string, value: unknown) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* quota / private mode */
  }
}

export function cacheJobSummary(job: CachedJobSummary) {
  if (!job?.id) return
  writeJson(`${SUMMARY_PREFIX}${job.id}`, job)
}

export function readCachedJobSummary(jobId: string): CachedJobSummary | null {
  if (!jobId) return null
  return readJson<CachedJobSummary>(`${SUMMARY_PREFIX}${jobId}`)
}

export function cacheJobTasks(jobId: string, tasks: JobTask[]) {
  if (!jobId) return
  writeJson(`${TASKS_PREFIX}${jobId}`, tasks)
}

export function readCachedJobTasks(jobId: string): JobTask[] | null {
  if (!jobId) return null
  const rows = readJson<JobTask[]>(`${TASKS_PREFIX}${jobId}`)
  return Array.isArray(rows) ? rows : null
}

/** Persist optimistic task completion so offline reload keeps checkmarks. */
export function patchCachedJobTaskDone(jobId: string, taskId: string, isDone: boolean) {
  if (!jobId || !taskId) return
  const cached = readCachedJobTasks(jobId)
  if (!cached) return
  cacheJobTasks(
    jobId,
    cached.map((t) => (t.id === taskId ? { ...t, is_done: isDone } : t))
  )
}

export function cacheJobAttachments(jobId: string, attachments: JobAttachment[]) {
  if (!jobId) return
  writeJson(`${ATTACHMENTS_PREFIX}${jobId}`, attachments)
}

export function readCachedJobAttachments(jobId: string): JobAttachment[] | null {
  if (!jobId) return null
  const rows = readJson<JobAttachment[]>(`${ATTACHMENTS_PREFIX}${jobId}`)
  return Array.isArray(rows) ? rows : null
}
